// src/dnd/views/Sidebar_parts/MapControlsTab.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import LightingControls from "./LightingControls";
import AssetUploadModal from "./AssetUploadModal";

export default function MapControlsTab(props) {
  const {
    customCategories,
    customTypes,
    typeColours,
    visibleTypes,
    visibleCategories,
    expandedCategories,
    setExpandedCategories,
    expandedTypes,
    setExpandedTypes,
    toggleAllTypes,
    allTypesVisible,
    toggleCategoryExpand,
    toggleCategory,
    toggleType,
    pois,
    zones = [],
    visibleZones,
    setVisibleZones,
    hoveredZoneId,
    setHoveredZoneId,
    poiLabelToggleBtn,
    isMobile = false,
    onMobileTypeClick,
    onClose,
    onUserLogin,
    hoveredType,
    hoveredPoiId,
    setHoveredPoiId,
    handleTypeHover,
    handleTypeUnhover,
    setHideSidebarHover,
    isMapOwner,
    extraRegionMaps = [],
    onAddNewRegionMap,
    onRegionMapClick,
    parentMapId,
    mapId,
    styles = {},
    TIP_BAR_HEIGHT = 68,
    mapDisplayName,
    showAllPoiLabels = false,
    showPoiNames = false,
    setShowPoiNames = () => {},
    user,
    globalLightOpacity = null,
    setGlobalLightOpacity = () => {},
    onSaveGlobalLight,
    onSaveLighting,
    proximityVoiceEnabled = false,
    proximityVoiceSaving = false,
    proximityVoiceStatus = "",
    onToggleProximityVoice = null,
  } = props;

  const fontColour = styles.font_colour || "#fff";
  const mainFont = styles.font_family || "inherit";

  const params = useParams();
  const routeIdRaw = params?.id ?? params?.mapId ?? params?.map_id;
  let routeId = null;
  if (routeIdRaw != null) {
    if (typeof routeIdRaw === "string") {
      const m = routeIdRaw.match(/(\d+)$/);
      if (m) routeId = Number(m[1]);
    } else if (!Number.isNaN(Number(routeIdRaw))) {
      routeId = Number(routeIdRaw);
    }
  }
  const resolvedMapId = mapId ?? props.mapData?.id ?? routeId ?? null;

  const [zonesExpanded, setZonesExpanded] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [newMapName, setNewMapName] = useState("");
  const [newMapFile, setNewMapFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [editingMapId, setEditingMapId] = useState(null);
  const [editingMapName, setEditingMapName] = useState("");

  const sectionNameZones = styles.section_name_zones || "Zones";
  const sectionNameRegionMaps = styles.section_name_region_maps || "Region Maps";

  const hasTitleImage = Boolean(styles?.title_image_url);
  const allZonesVisible = zones.length > 0 && zones.every((z) => visibleZones.has(z.id));

  const toggleZoneVisibility = (zoneId) => {
    setVisibleZones((prev) => {
      const next = new Set(prev);
      if (next.has(zoneId)) next.delete(zoneId);
      else next.add(zoneId);
      return new Set(next);
    });
  };

  const selectAllZones = () => setVisibleZones(new Set(zones.map((z) => z.id)));
  const deselectAllZones = () => setVisibleZones(new Set());

  const handleAddNewMapClick = () => {
    setShowUploadForm(true);
    setNewMapName("");
    setNewMapFile(null);
    setUploadError(null);
  };

  const handleCancelUpload = () => {
    setShowUploadForm(false);
    setUploadError(null);
  };

  const handleFileChange = (e) => setNewMapFile(e.target.files[0]);

  const startEditingMap = (region) => {
    setEditingMapId(region.id);
    setEditingMapName(region.name);
  };

  const cancelEditing = () => {
    setEditingMapId(null);
    setEditingMapName("");
  };

  const saveEditingMap = async () => {
    if (!editingMapName.trim()) return;
    try {
      const res = await fetch(`/api/child-maps/${editingMapId}/child`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ display_name: editingMapName.trim(), uploader_id: user?.id }),
      });
      if (res.ok) {
        if (typeof onAddNewRegionMap === "function") onAddNewRegionMap();
        cancelEditing();
      } else {
        alert("Failed to update map name.");
      }
    } catch {
      alert("Error updating map name.");
    }
  };

  const deleteMap = async () => {
    if (!window.confirm("Are you sure you want to delete this map?")) return;
    try {
      const res = await fetch(`/api/child-maps/${editingMapId}/child`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ uploader_id: user?.id }),
      });
      if (res.ok) {
        if (typeof onAddNewRegionMap === "function") onAddNewRegionMap();
        cancelEditing();
      } else {
        alert("Failed to delete map.");
      }
    } catch {
      alert("Error deleting map.");
    }
  };

  const [rmCollapsed, setRmCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("mapControls_regionMapsCollapsed");
      return v ? v === "1" : true;
    } catch {
      return true;
    }
  });
  const [zonesCollapsed, setZonesCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("mapControls_zonesCollapsed");
      return v ? v === "1" : true;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("mapControls_regionMapsCollapsed", rmCollapsed ? "1" : "0");
    } catch {}
  }, [rmCollapsed]);
  useEffect(() => {
    try {
      localStorage.setItem("mapControls_zonesCollapsed", zonesCollapsed ? "1" : "0");
    } catch {}
  }, [zonesCollapsed]);

  const [assetsCollapsed, setAssetsCollapsed] = useState(() => {
    try {
      const v = localStorage.getItem("mapControls_assetsCollapsed");
      return v ? v === "1" : false;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("mapControls_assetsCollapsed", assetsCollapsed ? "1" : "0");
    } catch {}
  }, [assetsCollapsed]);

  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState(null);
  const [assetFilterType, setAssetFilterType] = useState("all");
  const [assetSearch, setAssetSearch] = useState("");
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [confirmAsset, setConfirmAsset] = useState(null);
  const [editingAssetId, setEditingAssetId] = useState(null);

  const safeReadJson = async (res) => {
    const ct = String(res.headers?.get?.("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
      try {
        return await res.json();
      } catch {
        return null;
      }
    }
    try {
      const txt = await res.text();
      return { _non_json: true, text: txt };
    } catch {
      return null;
    }
  };

  const loadAssets = async () => {
    if (!resolvedMapId) return;
    try {
      setAssetsLoading(true);
      setAssetsError(null);
      const q = new URLSearchParams();
      if (assetFilterType !== "all") q.set("type", assetFilterType);
      if (assetSearch.trim()) q.set("q", assetSearch.trim());
      const url = `/api/tabletop/maps/${resolvedMapId}/assets?${q.toString()}`;
      const res = await fetch(url, { credentials: "include" });
      const data = await safeReadJson(res);
      if (!res.ok) {
        const msg =
          (data && data.error) ||
          (data && data._non_json && data.text) ||
          "Failed to load assets";
        throw new Error(msg);
      }
      setAssets(Array.isArray(data) ? data : []);
    } catch (e) {
      setAssetsError(String(e.message || e));
    } finally {
      setAssetsLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, [resolvedMapId]);
  useEffect(() => {
    if (assetsCollapsed === false) loadAssets();
  }, [assetsCollapsed]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!assetsCollapsed) loadAssets();
    }, 300);
    return () => clearTimeout(t);
  }, [assetFilterType, assetSearch, assetsCollapsed]);

  const broadcastAssetsUpdate = React.useCallback(() => {
    if (!resolvedMapId) return;
    try {
      window.dispatchEvent(
        new CustomEvent("tabletop-assets-updated", { detail: { mapId: resolvedMapId } })
      );
    } catch {}
  }, [resolvedMapId]);

  const updateAsset = async (id, patch) => {
    try {
      const res = await fetch(`/api/tabletop/assets/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(patch),
      });
      const data = await safeReadJson(res);
      if (!res.ok) {
        const msg =
          (data && data.error) || (data && data._non_json && data.text) || "Update failed";
        throw new Error(msg);
      }
      setAssets((prev) => prev.map((a) => (a.id === id ? data : a)));
      broadcastAssetsUpdate();
    } catch (e) {
      alert(String(e.message || e));
    }
  };

  const deleteAssetById = async (id) => {
    try {
      const res = await fetch(`/api/tabletop/assets/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await safeReadJson(res);
      if (!res.ok) {
        const msg =
          (data && data.error) || (data && data._non_json && data.text) || "Delete failed";
        throw new Error(msg);
      }
      setAssets((prev) => prev.filter((a) => a.id !== id));
      if (editingAssetId === id) {
        setEditingAssetId(null);
        try {
          window.dispatchEvent(
            new CustomEvent("tabletop-edit-asset", {
              detail: { mapId: resolvedMapId, assetId: null },
            })
          );
        } catch {}
      }
      broadcastAssetsUpdate();
    } catch (e) {
      alert(String(e.message || e));
    }
  };

  const addAssetToMap = async (asset) => {
    if (!resolvedMapId) return;
    try {
      const payload = {
        map_id: resolvedMapId,
        asset_type: asset.asset_type || asset.type || "Image",
        name:
          asset.name ||
          asset.base_name ||
          asset.originalname ||
          (asset.title || "Untitled"),
        url: asset.url,
        visible: 1,
        opacity: asset.opacity ?? 1,
        z_index: asset.z_index ?? 0,
        blocks_vision: asset.blocks_vision ?? 0,
        los_points: asset.los_points ?? null,
      };
      const res = await fetch(`/api/tabletop/assets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await safeReadJson(res);
      if (!res.ok) {
        const msg =
          (data && data.error) || (data && data._non_json && data.text) || "Add failed";
        throw new Error(msg);
      }
      setAssets((prev) => [data, ...prev]);
      broadcastAssetsUpdate();
    } catch (e) {
      alert(String(e.message || e));
    }
  };

  const duplicateAsset = async (asset) => {
    await addAssetToMap(asset);
  };

  const getAssetTitle = (a) =>
    a?.title ||
    a?.name ||
    a?.base_name ||
    a?.original_filename ||
    a?.originalname ||
    "Untitled";

  const isImageUrl = (u) =>
    typeof u === "string" && /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(u);

  const onDragStartAsset = (e, asset) => {
    try {
      const payload = {
        kind: "pointatlas-asset",
        map_id: resolvedMapId,
        id: asset.id,
        asset_type: asset.asset_type || asset.type || "overlay_image",
        name: getAssetTitle(asset),
        url: asset.url || "",
        opacity: asset.opacity ?? 1,
        z_index: asset.z_index ?? 0,
        blocks_vision: asset.blocks_vision ?? 0,
        los_points: asset.los_points ?? null,
      };
      const json = JSON.stringify(payload);
      e.dataTransfer.setData("application/pointatlas-asset", json);
      e.dataTransfer.setData("text/pointatlas-asset", json);
      e.dataTransfer.setData("text/plain", json);
      e.dataTransfer.effectAllowed = "copy";
    } catch {}
  };

  const startEditingAssetOnMap = (asset) => {
    setEditingAssetId((prevId) => {
      const nextId = prevId === asset.id ? null : asset.id;
      try {
        window.dispatchEvent(
          new CustomEvent("tabletop-edit-asset", {
            detail: { mapId: resolvedMapId, assetId: nextId },
          })
        );
      } catch {}
      return nextId;
    });
  };

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        background: "transparent",
        paddingBottom: `${TIP_BAR_HEIGHT + 8}px`,
      }}
    >
      <LightingControls
        key={resolvedMapId || "no-map"}
        styles={styles}
        mapId={resolvedMapId}
        user={user}
        globalLightOpacity={globalLightOpacity}
        setGlobalLightOpacity={setGlobalLightOpacity}
        onSaveGlobalLight={onSaveGlobalLight}
        onSaveLighting={onSaveLighting}
      />

      {isMapOwner && resolvedMapId && (
        <div
          style={{
            margin: "8px 12px 14px 12px",
            borderRadius: 10,
            background: "#1f2027",
            color: "#fff",
            padding: "12px 14px",
            boxShadow: "0 4px 12px #0004",
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 6,
            }}
          >
            Voice
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: proximityVoiceSaving ? "wait" : "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={!!proximityVoiceEnabled}
              disabled={proximityVoiceSaving}
              onChange={(e) => {
                if (typeof onToggleProximityVoice === "function") {
                  onToggleProximityVoice(e.target.checked);
                }
              }}
              style={{ transform: "scale(1.15)" }}
            />
            <span style={{ fontWeight: 700 }}>
              Enable proximity voice
            </span>
          </label>

          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: proximityVoiceStatus
                ? proximityVoiceStatus.toLowerCase().includes("failed") ||
                  proximityVoiceStatus.toLowerCase().includes("error")
                  ? "#ff8f8f"
                  : "#b7ffbe"
                : "#b9beca",
              minHeight: 18,
            }}
          >
            {proximityVoiceStatus || "When enabled, linked Voxemera voice uses map proximity."}
          </div>
        </div>
      )}

      {(isMapOwner || extraRegionMaps.length > 0) && (
        <div
          style={{
            margin: "8px 12px 14px 12px",
            borderRadius: 10,
            background: "#1f2027",
            color: "#fff",
          }}
        >
          <button
            onClick={() => setRmCollapsed((c) => !c)}
            aria-expanded={!rmCollapsed}
            style={{
              width: "100%",
              textAlign: "left",
              background: "transparent",
              color: "#fff",
              border: "none",
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            <span
              style={{
                display: "inline-block",
                transform: rmCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                transition: "transform 0.15s",
              }}
            >
              ▸
            </span>
            {sectionNameRegionMaps}
          </button>

          <div
            id="region-maps-drawer"
            style={{
              overflow: "hidden",
              maxHeight: rmCollapsed ? 0 : 2000,
              transition: "max-height 220ms ease",
              padding: rmCollapsed ? "0 12px" : "0 12px 12px 12px",
            }}
          >
            <div
              style={{
                backgroundColor: "#282931",
                borderRadius: 8,
                color: "#eee",
                fontSize: 14,
                padding: "10px 14px",
                boxShadow: "0 4px 12px #0004",
              }}
            >
              {extraRegionMaps.length === 0 && !showUploadForm && (
                <div
                  style={{
                    fontStyle: "italic",
                    marginBottom: 10,
                    color: "#999",
                    userSelect: "none",
                  }}
                >
                  No region maps yet
                </div>
              )}

              {!showUploadForm && (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  <li
                    key="parent-map"
                    style={{
                      padding: "6px 0",
                      cursor: parentMapId ? "pointer" : "default",
                      fontWeight: "bold",
                      color: "#ffd870",
                      background:
                        !extraRegionMaps.some((r) => r.id === parentMapId) && parentMapId
                          ? "#403300"
                          : "transparent",
                      borderRadius: 5,
                      opacity: 1,
                    }}
                    onClick={() => {
                      if (onRegionMapClick && parentMapId) {
                        onRegionMapClick({
                          id: parentMapId,
                          name: mapDisplayName,
                          isParent: true,
                        });
                      }
                    }}
                  >
                    {mapDisplayName}{" "}
                    {extraRegionMaps.some((r) => r.id === parentMapId) ? "(Parent)" : ""}
                  </li>

                  {extraRegionMaps.map((region) => {
                    const isEditing = editingMapId === region.id;
                    return (
                      <li
                        key={region.id}
                        style={{
                          padding: "6px 0",
                          cursor: region.id === parentMapId ? "default" : "pointer",
                          background: region.id === parentMapId ? "#ffd87033" : "transparent",
                          color: "#fff",
                          fontWeight: region.id === parentMapId ? 700 : 400,
                          borderRadius: 5,
                          opacity: region.id === parentMapId ? 0.85 : 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                        onClick={() => {
                          if (!isEditing && onRegionMapClick && region.id !== parentMapId) {
                            onRegionMapClick(region);
                          }
                        }}
                      >
                        {!isEditing ? (
                          <>
                            <span
                              style={{
                                flexGrow: 1,
                                cursor: region.id === parentMapId ? "default" : "pointer",
                              }}
                            >
                              {region.name}
                            </span>
                            {isMapOwner && region.id !== parentMapId && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingMap(region);
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#ffc73c",
                                  fontSize: 16,
                                  padding: 0,
                                }}
                                aria-label={`Edit map ${region.name}`}
                                title={`Edit map ${region.name}`}
                              >
                                🖉
                              </button>
                            )}
                          </>
                        ) : (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelEditing();
                              }}
                              style={{
                                backgroundColor: "#888",
                                border: "none",
                                borderRadius: 4,
                                padding: "4px 8px",
                                color: "#fff",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                              aria-label="Cancel"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteMap();
                              }}
                              style={{
                                backgroundColor: "#ff5959",
                                border: "none",
                                borderRadius: 4,
                                padding: "4px 8px",
                                color: "#fff",
                                fontWeight: "bold",
                                cursor: "pointer",
                              }}
                              aria-label="Delete"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}

                  {isMapOwner && (
                    <li
                      style={{
                        padding: "6px 0",
                        cursor: "pointer",
                        fontWeight: "bold",
                        borderTop: "1px solid #444",
                        marginTop: 8,
                        color: "#ffd870",
                      }}
                      onClick={handleAddNewMapClick}
                    >
                      + Add New Map
                    </li>
                  )}
                </ul>
              )}

              {showUploadForm && (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!newMapName.trim() || !newMapFile) {
                      setUploadError("Please provide a map name and select a file.");
                      return;
                    }
                    if (!parentMapId) {
                      setUploadError("Parent map ID is missing.");
                      return;
                    }
                    if (newMapFile.size > 10 * 1024 * 1024) {
                      setUploadError("File size exceeds 10MB limit.");
                      return;
                    }
                    setUploading(true);
                    setUploadError(null);
                    try {
                      const formData = new FormData();
                      formData.append("display_name", newMapName.trim());
                      formData.append("uploader_id", user?.id);
                      formData.append("map", newMapFile);
                      const response = await fetch(`/api/child-maps/${parentMapId}/child`, {
                        method: "POST",
                        body: formData,
                        credentials: "include",
                      });
                      const data = await safeReadJson(response);
                      if (!response.ok) {
                        const msg =
                          (data && data.error) ||
                          (data && data._non_json && data.text) ||
                          "Upload failed";
                        setUploadError(msg);
                      } else {
                        setShowUploadForm(false);
                        setNewMapName("");
                        setNewMapFile(null);
                        if (typeof onAddNewRegionMap === "function") onAddNewRegionMap();
                      }
                    } catch {
                      setUploadError("An error occurred during upload.");
                    } finally {
                      setUploading(false);
                    }
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    marginTop: 8,
                  }}
                >
                  <input
                    type="text"
                    placeholder="Map Name"
                    value={newMapName}
                    onChange={(e) => setNewMapName(e.target.value)}
                    disabled={uploading}
                    required
                    style={{
                      padding: "6px 8px",
                      borderRadius: 6,
                      border: "1.5px solid #444",
                      fontSize: 14,
                      backgroundColor: "#222",
                      color: "#eee",
                    }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                    required
                    style={{ color: "#eee" }}
                  />
                  {uploadError && (
                    <div
                      style={{
                        color: "#ff5959",
                        fontWeight: "bold",
                        fontSize: 13,
                      }}
                    >
                      {uploadError}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="submit"
                      disabled={uploading}
                      style={{
                        backgroundColor: "#ffc73c",
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 16px",
                        fontWeight: 700,
                        cursor: uploading ? "wait" : "pointer",
                        flexGrow: 1,
                        color: "#222",
                        fontSize: 16,
                      }}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      disabled={uploading}
                      style={{
                        backgroundColor: "#444",
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 16px",
                        fontWeight: 700,
                        cursor: uploading ? "wait" : "pointer",
                        flexGrow: 1,
                        color: "#eee",
                        fontSize: 16,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          margin: "8px 12px 14px 12px",
          borderRadius: 10,
          background: "#1f2027",
          color: "#fff",
        }}
      >
        <button
          onClick={() => setAssetsCollapsed((c) => !c)}
          aria-expanded={!assetsCollapsed}
          style={{
            width: "100%",
            textAlign: "left",
            background: "transparent",
            color: "#fff",
            border: "none",
            padding: "10px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontWeight: 800,
            fontSize: 16,
          }}
        >
          <span
            style={{
              display: "inline-block",
              transform: assetsCollapsed ? "rotate(0deg)" : "rotate(90deg)",
              transition: "transform 0.15s",
            }}
          >
            ▸
          </span>
          Assets
        </button>

        <div
          style={{
            overflow: "hidden",
            maxHeight: assetsCollapsed ? 0 : 2000,
            transition: "max-height 220ms ease",
            padding: assetsCollapsed ? "0 12px" : "0 12px 12px 12px",
          }}
        >
          <div style={{ background: "#242632", borderRadius: 8, padding: 10 }}>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <select
                value={assetFilterType}
                onChange={(e) => setAssetFilterType(e.target.value)}
                style={{
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: "#282931",
                  color: "#fff",
                  border: "1px solid #3a3b44",
                }}
              >
                <option value="all">All types</option>
                <option value="overlay_image">Overlay Image</option>
                <option value="roof_overlay">Roof/Ceiling</option>
                <option value="obstacle">Obstacle/Wall Sprite</option>
                <option value="icon_sprite">Icon/Sprite</option>
                <option value="sfx_audio">Sound Effect</option>
              </select>
              <input
                type="search"
                placeholder="Search name..."
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                style={{
                  flex: 1,
                  minWidth: 160,
                  padding: "6px 8px",
                  borderRadius: 6,
                  background: "#282931",
                  color: "#fff",
                  border: "1px solid #3a3b44",
                }}
              />
              <button
                onClick={() => setShowAssetModal(true)}
                style={{
                  background: "#ffc73c",
                  color: "#222",
                  fontWeight: 800,
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
                title="Upload or create a new asset"
              >
                ＋ Upload/Create Asset
              </button>
              <button
                onClick={loadAssets}
                title="Refresh"
                style={{
                  background: "#383a46",
                  color: "#fff",
                  fontWeight: 800,
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: "pointer",
                }}
              >
                ↻
              </button>
            </div>

            {assetsLoading && (
              <div style={{ color: "#bbb", fontStyle: "italic" }}>Loading...</div>
            )}
            {assetsError && (
              <div style={{ color: "#ff6b6b", fontWeight: 700 }}>{assetsError}</div>
            )}

            {!assetsLoading && !assetsError && (
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: 10,
                }}
              >
                {assets.map((a) => {
                  const title = getAssetTitle(a);
                  const isImage = isImageUrl(a.url);
                  const isEditing = editingAssetId === a.id;
                  return (
                    <li key={a.id} style={{ minWidth: 0 }}>
                      <div
                        draggable
                        onDragStart={(e) => onDragStartAsset(e, a)}
                        onClick={() => startEditingAssetOnMap(a)}
                        title={title}
                        style={{
                          position: "relative",
                          background: isEditing ? "#2f3344" : "#1f2027",
                          border: isEditing ? "2px solid #ffc73c" : "1px solid #32333d",
                          borderRadius: 10,
                          padding: 6,
                          cursor: "grab",
                          boxShadow: "0 2px 6px #0005",
                          userSelect: "none",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            zIndex: 5,
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmAsset(a);
                            }}
                            title="Delete asset"
                            style={{
                              background: "#3a1a1a",
                              border: "none",
                              borderRadius: 6,
                              padding: "2px 5px",
                              color: "#ff8080",
                              fontSize: 11,
                              cursor: "pointer",
                            }}
                          >
                            🗑
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              duplicateAsset(a);
                            }}
                            title="Duplicate asset"
                            style={{
                              background: "#27332f",
                              border: "none",
                              borderRadius: 6,
                              padding: "2px 5px",
                              color: "#9effc0",
                              fontSize: 11,
                              cursor: "pointer",
                            }}
                          >
                            ⧉
                          </button>
                        </div>

                        <div
                          style={{
                            fontWeight: 800,
                            color: "#fff",
                            fontSize: 11,
                            marginBottom: 4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingRight: 28,
                          }}
                        >
                          {title}
                        </div>
                        <div
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            background: "#14151a",
                            borderRadius: 8,
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {isImage ? (
                            <img
                              src={a.url}
                              alt={title}
                              draggable={false}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                pointerEvents: "none",
                              }}
                            />
                          ) : (
                            <span role="img" aria-label="file" style={{ fontSize: 22 }}>
                              📄
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
                {assets.length === 0 && (
                  <li style={{ color: "#999", fontStyle: "italic" }}>No assets yet.</li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      {zones.length > 0 && (
        <div
          style={{
            margin: "8px 12px 14px 12px",
            borderRadius: 10,
            background: "#1f2027",
            color: "#fff",
            userSelect: "none",
          }}
        >
          <button
            onClick={() => setZonesCollapsed((c) => !c)}
            aria-expanded={!zonesCollapsed}
            style={{
              width: "100%",
              textAlign: "left",
              background: "transparent",
              color: "#fff",
              border: "none",
              padding: "10px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            <span
              style={{
                display: "inline-block",
                transform: zonesCollapsed ? "rotate(0deg)" : "rotate(90deg)",
                transition: "transform 0.15s",
              }}
            >
              ▸
            </span>
            {sectionNameZones}
          </button>

          <div
            style={{
              overflow: "hidden",
              maxHeight: zonesCollapsed ? 0 : 2000,
              transition: "max-height 220ms ease",
              padding: zonesCollapsed ? "0 12px" : "0 12px 12px 12px",
            }}
          >
            <div style={{ background: "#242632", borderRadius: 8, padding: 10 }}>
              <div style={{ margin: "8px 0 4px 0", display: "flex", gap: 8 }}>
                <button
                  onClick={allZonesVisible ? deselectAllZones : selectAllZones}
                  style={{
                    fontSize: 14,
                    padding: "2px 13px",
                    borderRadius: 4,
                    background: "#282931",
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                    marginRight: 4,
                  }}
                >
                  {allZonesVisible ? "Hide All" : "Show All"}
                </button>
                <button
                  onClick={() => setZonesExpanded(!zonesExpanded)}
                  title={zonesExpanded ? "Collapse Zones" : "Expand Zones"}
                  style={{
                    fontSize: 18,
                    lineHeight: 1,
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: "#282931",
                    color: "#fff",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {zonesExpanded ? "−" : "+"}
                </button>
              </div>

              {zonesExpanded && (
                <ul style={{ paddingLeft: 12, marginTop: 6 }}>
                  {zones.map((zone) => {
                    const label = zone.label?.trim() || "Untitled";
                    const checked = visibleZones.has(zone.id);
                    return (
                      <li
                        key={zone.id}
                        style={{
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                        }}
                        onMouseEnter={() => setHoveredZoneId && setHoveredZoneId(zone.id)}
                        onMouseLeave={() => setHoveredZoneId && setHoveredZoneId(null)}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleZoneVisibility(zone.id)}
                          style={{ marginRight: 8 }}
                          id={`zone-vis-${zone.id}`}
                        />
                        <label
                          htmlFor={`zone-vis-${zone.id}`}
                          style={{
                            cursor: "pointer",
                            flexGrow: 1,
                            userSelect: "none",
                          }}
                        >
                          {label}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      <AssetUploadModal
        isOpen={showAssetModal}
        onClose={() => setShowAssetModal(false)}
        onCreated={(a) => {
          setAssets((prev) => [a, ...prev]);
          broadcastAssetsUpdate();
        }}
        mapId={resolvedMapId}
        user={user}
      />

      {confirmAsset && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirmAsset(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "#0008",
            zIndex: 3500,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#1f2027",
              color: "#fff",
              borderRadius: 12,
              padding: 16,
              width: 320,
              maxWidth: "92vw",
              boxShadow: "0 10px 28px #000b",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 10, fontSize: 16 }}>
              Delete this asset?
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#ccc",
                marginBottom: 14,
                wordBreak: "break-word",
              }}
            >
              {getAssetTitle(confirmAsset)}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmAsset(null)}
                style={{
                  background: "#444",
                  border: "none",
                  borderRadius: 8,
                  padding: "8px 12px",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const id = confirmAsset.id;
                  setConfirmAsset(null);
                  await deleteAssetById(id);
                }}
                style={{
                  background: "#ff5959",
                  border: "none",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}